// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/SpreadsheetCitationContextPanel.test.tsx
import { Table } from "@phosphor-icons/react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { SpreadsheetCitationDialog } from "./SpreadsheetCitationDialog";

import { Preview } from "../../../components/Preview";
import {
  citedRowToKnowledgeFields,
  filenameFromSpreadsheetUrl,
  parseWorkbookFromArrayBuffer,
  type SpreadsheetCitationAnchor,
} from "../../../components/spreadsheetWorkbook";
import { getSignedPdfUrl } from "../../../services/files";
import { type KnowledgeDataRecordProperty } from "../../../utils/parsers/knowledge-db";

export type SpreadsheetCitationSelection = {
  sourceUrl: string;
  cellAnchor: SpreadsheetCitationAnchor;
  fileName?: string;
};

/**
 * Claims-style summary card for a spreadsheet citation in the Ask Puntt
 * context pane. Replaces the cramped inline SpreadsheetView; full grid with
 * cell highlight opens in SpreadsheetCitationDialog.
 */
export function SpreadsheetCitationContextPanel({
  citation,
  onClose,
}: {
  citation: SpreadsheetCitationSelection;
  onClose?: () => void;
}) {
  const { t } = useTranslation("ui");
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [fields, setFields] = useState<KnowledgeDataRecordProperty[]>([]);
  const [sheetName, setSheetName] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const displayName = useMemo(() => {
    if (citation.fileName?.trim()) return citation.fileName.trim();
    if (sheetName?.trim()) return sheetName.trim();
    return (
      filenameFromSpreadsheetUrl(citation.sourceUrl) ||
      t("spreadsheetCitationContextPanel.header")
    );
  }, [citation.fileName, citation.sourceUrl, sheetName, t]);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setFields([]);
    setSheetName(null);

    (async () => {
      try {
        const signed = await getSignedPdfUrl(citation.sourceUrl).catch(
          () => citation.sourceUrl,
        );
        const resp = await fetch(signed);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const buf = await resp.arrayBuffer();
        const workbook = await parseWorkbookFromArrayBuffer(buf);
        if (cancelled) return;
        const mapped = citedRowToKnowledgeFields(workbook, citation.cellAnchor);
        if (!mapped || mapped.fields.length === 0) {
          // Still show the panel chrome with sheet meta when the row is
          // empty — user can open the full sheet in the modal.
          setSheetName(
            mapped?.sheetName ?? citation.cellAnchor.sheetName ?? null,
          );
          setFields(mapped?.fields ?? []);
          setStatus("ready");
          return;
        }
        setSheetName(mapped.sheetName);
        setFields(mapped.fields);
        setStatus("ready");
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [citation.cellAnchor, citation.sourceUrl]);

  if (status === "loading") {
    return (
      <div
        className="flex min-h-0 flex-1 flex-col items-center justify-center p-6"
        data-testid="spreadsheet-citation-context-loading"
      >
        <Text size="2" className="text-puntt-neutral-gray-11">
          {t("spreadsheetCitationContextPanel.loading")}
        </Text>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div
        className="flex min-h-0 flex-1 flex-col gap-3 p-4"
        data-testid="spreadsheet-citation-context-error"
      >
        <Text size="2" className="text-puntt-neutral-gray-11">
          {t("spreadsheetCitationContextPanel.loadError")}
        </Text>
        {onClose ? (
          <Button
            size="1"
            variant="soft"
            color="gray"
            onClick={onClose}
            data-testid="spreadsheet-citation-context-back"
          >
            {t("spreadsheetCitationContextPanel.backToContextAssets")}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        data-testid="spreadsheet-citation-context-panel"
        aria-label={t("spreadsheetCitationContextPanel.ariaLabel")}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-puntt-neutral-gray-6 px-3 py-2">
          <Flex gap="2" align="center" className="min-w-0">
            <Table
              size={16}
              className="shrink-0 text-puntt-accent-11"
              aria-hidden
            />
            <Text
              size="2"
              weight="medium"
              className="truncate text-puntt-neutral-gray-12"
              title={displayName}
            >
              {displayName}
            </Text>
          </Flex>
          <Flex gap="2" align="center" className="shrink-0">
            <Button
              size="1"
              variant="outline"
              onClick={() => setDialogOpen(true)}
              data-testid="spreadsheet-citation-view-spreadsheet"
              aria-label={t("spreadsheetCitationContextPanel.viewSpreadsheet")}
            >
              <Text size="1">
                {t("spreadsheetCitationContextPanel.viewSpreadsheet")}
              </Text>
            </Button>
            {onClose ? (
              <Button
                size="1"
                variant="soft"
                color="gray"
                onClick={onClose}
                data-testid="spreadsheet-citation-context-back"
              >
                {t("spreadsheetCitationContextPanel.back")}
              </Button>
            ) : null}
          </Flex>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="rounded-md border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 p-3">
            {fields.length > 0 ? (
              <Preview knowledgeDataRecord={fields} />
            ) : (
              <Text size="2" className="text-puntt-neutral-gray-11">
                {t("spreadsheetCitationContextPanel.emptyRow")}
              </Text>
            )}
          </div>
        </div>
      </div>
      {dialogOpen ? (
        <SpreadsheetCitationDialog
          url={citation.sourceUrl}
          cellAnchor={citation.cellAnchor}
          fileName={displayName}
          onClose={() => setDialogOpen(false)}
        />
      ) : null}
    </>
  );
}
